import {
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react'
import { ChevronDownIcon } from './icons'

export function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ')
}

// ---------------------------------------------------------------------------
// Botão
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost'

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 rounded-xl font-medium transition ' +
  'disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.99]'

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'accent-gradient text-on-accent shadow-card hover:brightness-110 hover:shadow-lift font-semibold',
  secondary:
    'bg-surface-2 text-fg border border-border hover:border-border-strong hover:bg-surface',
  ghost: 'text-fg-muted hover:text-fg hover:bg-surface-2',
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  /** Alvo de toque grande, para a ação principal no celular. */
  size?: 'sm' | 'md' | 'lg'
}

export function Button({
  variant = 'secondary',
  size = 'md',
  className,
  ...props
}: ButtonProps) {
  const sizes = {
    sm: 'h-9 px-3 text-sm',
    md: 'h-11 px-4 text-sm',
    lg: 'h-14 px-6 text-base',
  }
  return (
    <button
      type="button"
      className={cx(BUTTON_BASE, BUTTON_VARIANTS[variant], sizes[size], className)}
      {...props}
    />
  )
}

/** Botão só de ícone — mantém 44 px de área de toque mesmo com ícone pequeno. */
export function IconButton({
  label,
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex size-11 shrink-0 items-center justify-center rounded-xl text-fg-muted',
        'transition hover:bg-surface-2 hover:text-fg active:scale-95',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Campo de texto
// ---------------------------------------------------------------------------

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label: string
  hint?: string
  error?: string
  /** Conteúdo à direita do campo (botão de revelar, impressão digital…). */
  trailing?: ReactNode
  /** Repassada ao <input>; React 19 trata ref como prop comum. */
  ref?: Ref<HTMLInputElement>
}

export function Field({ label, hint, error, trailing, id, ...props }: FieldProps) {
  const generatedId = useId()
  const fieldId = id ?? generatedId
  const hintId = `${fieldId}-hint`

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className="text-sm font-medium text-fg">
        {label}
      </label>
      <div
        className={cx(
          'flex items-center gap-1 rounded-xl border bg-surface-2 pr-1.5 transition',
          'focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/25',
          error ? 'border-danger' : 'border-border',
        )}
      >
        <input
          id={fieldId}
          aria-describedby={hint || error ? hintId : undefined}
          aria-invalid={error ? true : undefined}
          className="h-12 min-w-0 flex-1 bg-transparent px-3.5 text-base text-fg outline-none placeholder:text-fg-subtle"
          {...props}
        />
        {trailing}
      </div>
      {(error || hint) && (
        <p id={hintId} className={cx('text-xs', error ? 'text-danger' : 'text-fg-subtle')}>
          {error ?? hint}
        </p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Chip de seleção
// ---------------------------------------------------------------------------

export function Chip({
  selected,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { selected: boolean }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={cx(
        'h-10 rounded-full border px-4 text-sm font-medium whitespace-nowrap transition',
        selected
          ? 'border-transparent accent-gradient text-on-accent shadow-card'
          : 'border-border bg-surface-2 text-fg-muted hover:border-border-strong hover:text-fg',
        className,
      )}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// Interruptor
// ---------------------------------------------------------------------------

export function Switch({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  hint?: string
  disabled?: boolean
}) {
  const id = useId()
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <div className="min-w-0">
        <label htmlFor={id} className="block text-sm font-medium text-fg">
          {label}
        </label>
        {hint && <p className="mt-0.5 text-xs text-fg-subtle">{hint}</p>}
      </div>
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx(
          'relative mt-0.5 h-7 w-12 shrink-0 rounded-full border transition disabled:opacity-50',
          checked ? 'accent-gradient border-transparent' : 'border-border-strong bg-surface-2',
        )}
      >
        {/* Posições em px: com a borda de 1px, frações do Tailwind deixariam o
            botão levemente fora de centro dentro da pista. */}
        <span
          className={cx(
            'absolute top-[3px] size-5 rounded-full bg-white shadow transition-all',
            checked ? 'left-[23px]' : 'left-[3px]',
          )}
        />
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Seção recolhível
// ---------------------------------------------------------------------------

export function Disclosure({
  open,
  onToggle,
  label,
  icon,
  children,
}: {
  open: boolean
  onToggle: () => void
  label: string
  icon?: ReactNode
  children: ReactNode
}) {
  const id = useId()
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={id}
        className="flex w-full items-center justify-between gap-2 rounded-xl px-1 py-3 text-sm font-medium text-fg-muted transition hover:text-fg"
      >
        <span className="inline-flex items-center gap-2">
          {icon}
          {label}
        </span>
        <ChevronDownIcon
          className={cx('size-4 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div id={id} className="animate-rise pt-1">
          {children}
        </div>
      )}
    </div>
  )
}
